export interface MediaStreamConstraints {
  video?: boolean | MediaTrackConstraints;
  audio?: boolean | MediaTrackConstraints;
}

export interface GetUserMediaOptions {
  videoDeviceId?: string;
  audioDeviceId?: string;
  videoConstraints?: {
    width?: { min?: number; ideal?: number };
    height?: { min?: number; ideal?: number };
    frameRate?: { min?: number; ideal?: number };
  };
}

/**
 * Gets user media stream with specified device constraints
 */
export async function getUserMediaStream(
  options: GetUserMediaOptions = {}
): Promise<MediaStream> {
  const {
    videoDeviceId,
    audioDeviceId,
    videoConstraints = {
      width: { min: 640, ideal: 1280 },
      height: { min: 480, ideal: 720 },
      frameRate: { min: 15, ideal: 30 }
    }
  } = options;

  const constraints: MediaStreamConstraints = {
    video: videoDeviceId
      ? {
          deviceId: { exact: videoDeviceId },
          ...videoConstraints
        }
      : videoConstraints,
    audio: audioDeviceId
      ? {
          deviceId: { exact: audioDeviceId }
        }
      : true
  };

  console.log('🎥 Requesting media with constraints:', constraints);
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  console.log('🎥 Stream obtained:', {
    id: stream.id,
    active: stream.active,
    videoTracks: stream.getVideoTracks().length,
    audioTracks: stream.getAudioTracks().length
  });

  return stream;
}

/**
 * Stops all tracks in a media stream
 */
export function stopStreamTracks(stream: MediaStream | null): void {
  if (!stream) return;
  stream.getTracks().forEach(track => {
    try {
      track.stop();
    } catch (error) {
      console.warn('🔇 Failed to stop track:', error);
    }
  });
}

/**
 * Enables all video tracks in a stream
 */
export function enableVideoTracks(stream: MediaStream): void {
  stream.getVideoTracks().forEach(track => {
    track.enabled = true;
    console.log('🎥 Video track enabled:', {
      enabled: track.enabled,
      readyState: track.readyState,
      muted: track.muted,
      label: track.label
    });
  });
}

/**
 * Checks if a stream is active
 */
export function isStreamActive(stream: MediaStream | null): boolean {
  if (!stream) return false;
  return stream.active && stream.getVideoTracks().some(t => t.readyState === 'live');
}

/**
 * Assigns stream to video element and plays it
 */
export async function assignStreamToVideoElement(
  videoElement: HTMLVideoElement | null,
  stream: MediaStream,
  options: {
    clearExisting?: boolean;
    retryCount?: number;
    retryDelay?: number;
  } = {}
): Promise<void> {
  if (!videoElement) {
    throw new Error('Video element is null');
  }

  const {
    clearExisting = true,
    retryCount = 3,
    retryDelay = 200
  } = options;

  // Clear existing stream if needed
  if (clearExisting && videoElement.srcObject) {
    const oldStream = videoElement.srcObject as MediaStream;
    if (oldStream.id !== stream.id && oldStream.active) {
      console.log('🎥 Clearing old stream before assigning new one');
      stopStreamTracks(oldStream);
      videoElement.srcObject = null;
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  // Verify stream is active
  if (!stream.active) {
    throw new Error('Stream is not active');
  }

  // Enable video tracks
  enableVideoTracks(stream);

  // Assign stream
  videoElement.srcObject = stream;
  console.log('🎥 Stream assigned to video element');

  // Play with retry logic
  const playWithRetry = async (retries: number): Promise<void> => {
    try {
      if (videoElement && videoElement.srcObject === stream) {
        const assignedStream = videoElement.srcObject as MediaStream;
        if (!assignedStream.active) {
          throw new Error('Stream became inactive before play');
        }
        await videoElement.play();
        console.log('🎥 Video play successful');
      }
    } catch (error: any) {
      console.warn('🎥 Video play failed:', error);
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return playWithRetry(retries - 1);
      }
      throw error;
    }
  };

  // Wait for metadata if needed
  if (videoElement.readyState >= 2) {
    await playWithRetry(retryCount);
  } else {
    const onMetadataLoaded = async () => {
      console.log('🎥 Video metadata loaded, attempting play');
      await playWithRetry(retryCount);
      videoElement.removeEventListener('loadedmetadata', onMetadataLoaded);
    };
    videoElement.addEventListener('loadedmetadata', onMetadataLoaded);
  }
}

/**
 * Gets stream information for debugging
 */
export function getStreamInfo(stream: MediaStream | null): {
  id: string | null;
  active: boolean;
  videoTracks: number;
  audioTracks: number;
  videoTrackInfo: Array<{
    enabled: boolean;
    readyState: MediaStreamTrackState;
    muted: boolean;
    label: string;
  }>;
} {
  if (!stream) {
    return {
      id: null,
      active: false,
      videoTracks: 0,
      audioTracks: 0,
      videoTrackInfo: []
    };
  }

  return {
    id: stream.id,
    active: stream.active,
    videoTracks: stream.getVideoTracks().length,
    audioTracks: stream.getAudioTracks().length,
    videoTrackInfo: stream.getVideoTracks().map(track => ({
      enabled: track.enabled,
      readyState: track.readyState,
      muted: track.muted,
      label: track.label
    }))
  };
}



